import { c as _c } from "react/compiler-runtime";
import { useMemberSelectionDisplay } from "@components/common/member-autocomplete/use-member-selection-display";
import { EmailIcon, MemberAutocompleteSelection, grey, SingleSelectAutocomplete, TeamIcon } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { EmailUtils } from "@stellar/utils";
import { DEFAULT_FONT_FAMILY_ITALIC } from "@styles/common-styles";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getPrettyName } from "@utils/user-utils";
import { useEffect, useMemo, useState } from "react";

/** Re-export type so that we can import SelectedMember from this component.  */

/**
 * Defines the maximum items to be displayed in the autocomplete menu as suggestions.
 * The number should be low enough to fit them all at once in the screen.
 */
const MAX_ITEMS_TO_DISPLAY = 5;

/**
 * Height of the input, which the `MemberAutocompleteSelection` overlay rendered by
 * `renderSelection` fills. It needs to fit an avatar and two lines of text.
 */
const SELECTION_HEIGHT = "65px";

/**
 * Maps a member from the store to an option understood by the autocomplete.
 *
 * @param user The member to map.
 * @returns The option representing the member in the dropdown.
 */
function mapMemberToOption(user) {
  if (!FaroCoreApiUtils.Member.isMemberActive(user)) {
    return {
      title: getPrettyName(user),
      value: user.identity,
      avatarIcon: <EmailIcon />
    };
  }
  if (FaroCoreApiUtils.Member.isTeam(user)) {
    return {
      title: getPrettyName(user),
      subtitle: capitalizeFirstLetter(TEAM_DISPLAY_NAME),
      value: user.identity,
      avatarIcon: <TeamIcon />,
      avatarStyle: {
        backgroundColor: grey[700]
      }
    };
  }
  return {
    title: getPrettyName(user),
    subtitle: user.email,
    avatarImage: user.thumbnailUrl,
    value: user.identity
  };
}

/**
 * Maps the currently selected member to the value understood by the autocomplete.
 *
 * @param member The selected member, a typed email, or null.
 * @returns The option to display as selected, or null when nothing is selected.
 */
function mapSelectedMemberToValue(member) {
  if (!member) {
    return null;
  }
  if (typeof member === "string") {
    return {
      title: member,
      value: member
    };
  }
  return {
    title: getPrettyName(member),
    subtitle: FaroCoreApiUtils.Member.isMemberActive(member) ? member.email : undefined,
    value: member.identity
  };
}

/**
 * This component shows an autocomplete selection that let's the user to
 * select one member from a list, and optionally type a valid email.
 * The component is generic enough that can be used for any purpose regarding
 * selecting a single member. For selecting multiple members see the flat-ui
 * `Autocomplete` component (see `useMemberInviteAutocomplete`).
 */
export function MemberAutocomplete(t0) {
  const $ = _c(60);
  const {
    members,
    selectedMember,
    placeholder,
    isDisabled: t1,
    onOptionChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const [inputValue, setInputValue] = useState("");
  let t2;
  if ($[0] !== members) {
    t2 = members.map(mapMemberToOption);
    $[0] = members;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [currentOptions, setCurrentOptions] = useState(t2);
  let t3;
  if ($[2] !== selectedMember) {
    t3 = mapSelectedMemberToValue(selectedMember);
    $[2] = selectedMember;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedValue = t3;
  const selectionDisplay = useMemberSelectionDisplay(selectedMember);
  let t4;
  bb0: {
    if (!EmailUtils.isValidEmail(inputValue)) {
      t4 = false;
      break bb0;
    }
    let t5;
    if ($[4] !== inputValue) {
      t5 = inputValue.toLowerCase();
      $[4] = inputValue;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    const inputLower = t5;
    let t6;
    if ($[6] !== inputLower || $[7] !== members) {
      t6 = members?.some(user => user.email.toLowerCase() === inputLower);
      $[6] = inputLower;
      $[7] = members;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t4 = !t6;
  }
  const isValidNewEmail = t4;
  let t5;
  let t6;
  if ($[9] !== members) {
    t5 = () => {
      setCurrentOptions(members.map(mapMemberToOption));
    };
    t6 = [members];
    $[9] = members;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== members) {
    t7 = function findMemberByEmail(email) {
      const lowerEmail = email.toLowerCase();
      return members.find(user_0 => user_0.email?.toLowerCase() === lowerEmail);
    };
    $[12] = members;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const findMemberByEmail = t7;
  let t8;
  if ($[14] !== currentOptions || $[15] !== onOptionChange) {
    t8 = function addUserIfValidEmail(inputText) {
      if (!EmailUtils.isValidEmail(inputText)) {
        return;
      }
      const newOption = {
        title: inputText,
        value: inputText
      };
      setCurrentOptions([...currentOptions, newOption]);
      onOptionChange(inputText);
    };
    $[14] = currentOptions;
    $[15] = onOptionChange;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const addUserIfValidEmail = t8;
  let t9;
  if ($[17] !== members) {
    t9 = function resolveSelection(optionValue) {
      return members.find(user_1 => user_1.identity === optionValue) ?? optionValue;
    };
    $[17] = members;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const resolveSelection = t9;
  let t10;
  if ($[19] !== onOptionChange || $[20] !== resolveSelection) {
    t10 = function onSelectionChange(newValue) {
      if (newValue && typeof newValue === "object") {
        onOptionChange(resolveSelection(newValue.value));
      } else {
        if (typeof newValue === "string") {
          onOptionChange(newValue);
        } else {
          onOptionChange(null);
        }
      }
    };
    $[19] = onOptionChange;
    $[20] = resolveSelection;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const onSelectionChange = t10;
  let t11;
  if ($[22] !== addUserIfValidEmail || $[23] !== findMemberByEmail || $[24] !== inputValue || $[25] !== onOptionChange) {
    t11 = function handleKeyPress(event) {
      if (event.key !== "Enter" || inputValue.trim() === "") {
        return;
      }
      const inputValueTrimmed = inputValue.trim().toLowerCase();
      const existingMember = findMemberByEmail(inputValueTrimmed);
      if (existingMember) {
        onOptionChange(existingMember);
      } else {
        addUserIfValidEmail(inputValueTrimmed);
        if (event.target instanceof HTMLElement) {
          event.target.blur();
        }
      }
      setInputValue("");
    };
    $[22] = addUserIfValidEmail;
    $[23] = findMemberByEmail;
    $[24] = inputValue;
    $[25] = onOptionChange;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleKeyPress = t11;
  let t12;
  if ($[27] !== addUserIfValidEmail || $[28] !== findMemberByEmail || $[29] !== inputValue) {
    t12 = function onBlur() {
      if (!inputValue) {
        return;
      }
      const inputValueTrimmed_0 = inputValue.trim().toLowerCase();
      if (!findMemberByEmail(inputValueTrimmed_0)) {
        addUserIfValidEmail(inputValueTrimmed_0);
      }
    };
    $[27] = addUserIfValidEmail;
    $[28] = findMemberByEmail;
    $[29] = inputValue;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const onBlur = t12;
  let t13;
  if ($[31] !== members || $[32] !== onOptionChange) {
    t13 = function clearSelection() {
      onOptionChange(null);
      setInputValue("");
      setCurrentOptions(members.map(mapMemberToOption));
    };
    $[31] = members;
    $[32] = onOptionChange;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const clearSelection = t13;
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = value => setInputValue(value);
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== clearSelection || $[36] !== isDisabled || $[37] !== placeholder || $[38] !== selectionDisplay) {
    t15 = () => <MemberAutocompleteSelection {...selectionDisplay} isDisabled={isDisabled} placeholder={placeholder} onClick={clearSelection} />;
    $[35] = clearSelection;
    $[36] = isDisabled;
    $[37] = placeholder;
    $[38] = selectionDisplay;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      "& .MuiOutlinedInput-root": {
        minHeight: SELECTION_HEIGHT,
        paddingY: 1.25
      }
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== inputValue || $[42] !== isValidNewEmail) {
    t17 = <Box component="div">{isValidNewEmail ? <Box component="div" sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
        lineHeight: "24px",
        display: "-webkit-box",
        WebkitLineClamp: "3",
        WebkitBoxOrient: "vertical"
      }}>Type enter to invite{" "}<Box component="span" sx={{
          fontFamily: DEFAULT_FONT_FAMILY_ITALIC
        }}><var>{inputValue}</var></Box>{" "}to the workspace.</Box> : <>No members found. Please type a valid email to invite a new member to the workspace</>}</Box>;
    $[41] = inputValue;
    $[42] = isValidNewEmail;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== currentOptions || $[45] !== handleKeyPress || $[46] !== inputValue || $[47] !== isDisabled || $[48] !== onBlur || $[49] !== onSelectionChange || $[50] !== placeholder || $[51] !== selectedValue || $[52] !== t15 || $[53] !== t17) {
    t18 = <SingleSelectAutocomplete placeholder={placeholder} isDisabled={isDisabled} value={selectedValue} options={currentOptions} inputValue={inputValue} onInputChange={t14} onKeyDown={handleKeyPress} onBlur={onBlur} onChange={onSelectionChange} filterOptions={_temp} renderSelection={t15} inputSx={t16} noOptionsText={t17} />;
    $[44] = currentOptions;
    $[45] = handleKeyPress;
    $[46] = inputValue;
    $[47] = isDisabled;
    $[48] = onBlur;
    $[49] = onSelectionChange;
    $[50] = placeholder;
    $[51] = selectedValue;
    $[52] = t15;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== selectedMember) {
    t19 = typeof selectedMember === "string" && <Box component="div" sx={{
      fontSize: "12px",
      color: "text.primary",
      marginLeft: "4px",
      marginTop: "2px"
    }}>Inviting members by email will also invite them to the workspace</Box>;
    $[55] = selectedMember;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t18 || $[58] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  return t20;
}
function _temp(options, input) {
  const lowerInputValue = input.toLowerCase();
  const filteredOptions = options.filter(option => option.title.toLowerCase().includes(lowerInputValue) || option.subtitle?.toLowerCase().includes(lowerInputValue));
  return filteredOptions.slice(0, MAX_ITEMS_TO_DISPLAY);
}